import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'i18n'
import { DndContext, DragOverlay, rectIntersection } from '@dnd-kit/core'
import { snapCenterToCursor } from '@dnd-kit/modifiers'
import { DroppableSlot } from './components/DroppableSlot'
import { DraggableCard } from './components/DraggableCard'
import { StackedImage } from './components/StackedImage'
import { CardPlaceholder } from './components/CardPlaceholder'
import './DragAndDropGame.scss'
import './drag.scss'
import { submitMatchDrop, submitRanking } from 'services/drag-and-drop'
import { DragDropGameHeading } from './components/DragDropGameHeading'
import ShareIcons from 'components/ShareIcons'
import { ScrollableList } from 'components/scrollable/ScrollableList/ScrollableList'
import { getTrueFromMapper } from 'util/getTrueFromMapper'
import { EndGameModal } from './components/DragDropModal'
import { useModal } from 'hooks/useModal'
import { GAME_TYPE } from './constants'
import { submitOrder } from 'services/drag-and-drop'

const shuffleArray = array => {
  let shuffledArray = [...array]
  const originalIds = array.map(item => item.id)

  let isDeranged = false

  while (!isDeranged) {
    // Shuffle the array
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffledArray[i], shuffledArray[j]] = [
        shuffledArray[j],
        shuffledArray[i],
      ]
    }

    // Check if any card is still in its original position
    isDeranged = shuffledArray.every(
      (card, index) => card.id !== originalIds[index],
    )
  }

  return shuffledArray
}
const fixCursorSnapOffset = args => {
  if (!args.pointerCoordinates) {
    return rectIntersection(args)
  }
  const { x, y } = args.pointerCoordinates
  const { width, height } = args.collisionRect
  const updated = {
    ...args,
    collisionRect: {
      width,
      height,
      bottom: y + height / 2,
      left: x - width / 2,
      right: x + width / 2,
      top: y - height / 2,
    },
  }
  return rectIntersection(updated)
}

const DragAndDropGame = ({
  breakId,
  step,
  skipButton,
  nextStepButton,
  isEditorial,
  onSubmitMatches = () => {},
}) => {
  const { t } = useTranslation(['dragAndDrop', 'common'])
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false)
  const [cards, setCards] = useState([])
  const [backgrounds, setBackgrounds] = useState([])
  const [draggingCard, setDraggingCard] = useState(null)
  const [matchedPairs, setMatchedPairs] = useState({})
  const [validatedPairs, setValidatedPairs] = useState(null)
  const [checkingMatches, setCheckingMatches] = useState(false)
  const [hasDataError, setHasDataError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const { showModal, toggle } = useModal()

  const gameData = useMemo(() => step?.dragDropGame || {}, [step])
  const stacks = useMemo(() => gameData?.stacks || [], [gameData])
  const hasScoring = useMemo(() => stacks[0]?.hasScoring, [stacks])
  const shuffleSlots = useMemo(() => stacks[0]?.shuffleSlots, [stacks])

  const gameTypeMap = {
    [hasScoring && shuffleSlots]: GAME_TYPE.PAIRS,
    [hasScoring && !shuffleSlots]: GAME_TYPE.ORDER,
    [!hasScoring && !shuffleSlots]: GAME_TYPE.RANKING,
  }

  const gameType = getTrueFromMapper(gameTypeMap)

  const restartGame = () => {
    setMatchedPairs({})
    setValidatedPairs(null)
    setDraggingCard(null)
    setCheckingMatches(false)
    setShowCorrectAnswer(false)

    // Shuffle cards and ensure a new array is returned
    const shuffledCards = shuffleArray(cards).map((card, index) => {
      const updatedCard = {
        ...card,
        shuffleIndex: index, // Add an index as part of the shuffled key
        wasDropped: false, // Reset wasDropped state to ensure cards can be dragged
      }
      return updatedCard
    })

    // Set the shuffled cards to the state
    setCards(shuffledCards)

    // Shuffle backgrounds and ensure a new array is returned
    setBackgrounds(shuffleArray([...backgrounds]))
  }

  const socialShareComponent = step.socialShareOn && (
    <ShareIcons
      facebookShare={step.facebookShare}
      twitterShare={step.twitterShare}
      pinterestShare={step.pinterestShare}
      emailShare={step.emailShare}
      emailConfig={{
        subject: step.shareEmailSubject,
        body: step.shareEmailBody,
      }}
      copyShare={step.copyShare}
      whatsappShare={step.whatsappShare}
    />
  )

  useEffect(() => {
    const { cards } = stacks[0] || {}

    // Reset error state on new data
    setHasDataError(false)
    setErrorMessage('')

    // Validate cards data before processing
    if (!cards || !Array.isArray(cards) || cards.length === 0) {
      console.error(
        'BR-9645420: Invalid cards data - cards array is missing or empty',
      )
      setHasDataError(true)
      setErrorMessage('No card data available for this game.')
      return
    }

    // Check if all cards have valid cardImage and backgroundImage with ids
    const invalidCards = cards.filter(
      card =>
        !card.cardImage ||
        !card.backgroundImage ||
        !card.cardImage.id ||
        !card.backgroundImage.id ||
        typeof card.cardImage.id !== 'string' ||
        typeof card.backgroundImage.id !== 'string',
    )

    if (invalidCards.length > 0) {
      console.error('BR-9645421: Invalid card data detected:', {
        invalidCards,
        totalCardsCount: cards.length,
        invalidCardsCount: invalidCards.length,
      })

      setHasDataError(true)
      setErrorMessage(
        `Invalid drag and drop game data: ${invalidCards.length} out of ${cards.length} cards have missing or invalid cardImage/backgroundImage data. Please check the game configuration.`,
      )
      return
    }

    // Check for duplicate IDs which could also cause issues
    const cardImageIds = cards.map(item => item.cardImage.id)
    const backgroundImageIds = cards.map(item => item.backgroundImage.id)
    const duplicateCardIds = cardImageIds.filter(
      (id, index) => cardImageIds.indexOf(id) !== index,
    )
    const duplicateBackgroundIds = backgroundImageIds.filter(
      (id, index) => backgroundImageIds.indexOf(id) !== index,
    )

    if (duplicateCardIds.length > 0 || duplicateBackgroundIds.length > 0) {
      setHasDataError(true)
      setErrorMessage(
        `Invalid drag and drop game data: ${duplicateCardIds.length} out of ${cards.length} cards have duplicate cardImage/backgroundImage data. Please check the game configuration.`,
      )
      return
    }

    // Only proceed if data is valid
    try {
      setCards(shuffleArray(cards.map(item => item.cardImage)))
      setBackgrounds(shuffleArray(cards.map(item => item.backgroundImage)))
    } catch (shuffleError) {
      console.error('BR-9645423: Error shuffling cards:', shuffleError)
      setHasDataError(true)
      setErrorMessage(
        'Error processing game data. Please try refreshing the page.',
      )
    }
  }, [stacks])

  const [draggableCardsDimensions, setDraggableCardsDimensions] = useState({
    width: undefined,
    height: undefined,
  })

  const syncDimensionsWithGrid = useCallback(
    entries => {
      const entry = entries?.[0]

      if (!entry?.target) return

      const { width, height } = entry.target.getBoundingClientRect()
      setDraggableCardsDimensions({ width, height })
    },
    [setDraggableCardsDimensions],
  )

  useEffect(() => {
    syncDimensionsWithGrid()

    const gridChild = document.querySelector(
      '.drag_drop_container_match .drag-drop-grid-container > div:first-child',
    )

    if (!gridChild) return

    const resizeObserver = new ResizeObserver(syncDimensionsWithGrid)

    resizeObserver.observe(gridChild)

    return () => {
      resizeObserver.disconnect()
    }
  }, [cards, backgrounds, syncDimensionsWithGrid])

  // If there's a data error, render an error message instead of the game
  if (hasDataError) {
    return (
      <div className="drag_drop_container_match">
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            margin: '20px 0',
          }}
        >
          <h3 style={{ color: '#dc3545', marginBottom: '16px' }}>
            {t('dragAndDrop:error.configTitle')}
          </h3>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>
            {errorMessage}
          </p>
          <p style={{ fontSize: '14px', color: '#868e96' }}>
            {t('dragAndDrop:error.contactSupport')}
          </p>
        </div>
        {skipButton}
        {nextStepButton}
      </div>
    )
  }

  // Only render the game if we have valid data
  if (cards.length === 0 || backgrounds.length === 0) {
    return (
      <div className="drag_drop_container_match">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p>{t('dragAndDrop:loadingGame')}</p>
        </div>
      </div>
    )
  }

  // Handle drag start event
  const handleDragStart = event => {
    const draggedCard = cards.find(card => card.id === event.active.id)

    // Check if card is currently in a slot
    const isInSlot = Object.values(matchedPairs).some(
      card => card.id === event.active.id,
    )

    setDraggingCard({
      ...draggedCard,
      isComingFromBottom: !isInSlot,
      wasDropped: isInSlot,
    })
  }

  const handleDragEnd = event => {
    const { over } = event

    if (over && draggingCard) {
      const backgroundId = over.id
      const isComingFromBottom = !draggingCard.wasDropped
      const isOccupied = !!matchedPairs[backgroundId] // The destination slot ID

      // Log the result of the drag operation

      if (isComingFromBottom && isOccupied) {
        setDraggingCard(null)
        return // Exit early to prevent drop
      }

      // Check if the destination slot is empty
      if (!matchedPairs[backgroundId]) {
        // Move the card to the empty slot and leave the initial slot empty
        setMatchedPairs(prev => {
          const newMatchedPairs = { ...prev }

          // Find the initial slot of the dragged card and make it empty
          Object.keys(newMatchedPairs).forEach(slotId => {
            if (newMatchedPairs[slotId]?.id === draggingCard.id) {
              delete newMatchedPairs[slotId] // Clear the initial slot
            }
          })

          // Place the dragged card in the new (destination) slot
          newMatchedPairs[backgroundId] = draggingCard

          return newMatchedPairs
        })
      } else {
        // If the destination slot already has a card, swap the cards
        setMatchedPairs(prev => {
          const newMatchedPairs = { ...prev }

          // Find the initial slot of the dragged card
          Object.keys(newMatchedPairs).forEach(slotId => {
            if (newMatchedPairs[slotId]?.id === draggingCard.id) {
              // Swap the cards between the two slots
              newMatchedPairs[slotId] = newMatchedPairs[backgroundId]
            }
          })

          // Place the dragged card in the new slot (destination slot)
          newMatchedPairs[backgroundId] = draggingCard

          return newMatchedPairs
        })
      }

      // Reset the dragging state
      setDraggingCard(null)
    } else {
      // If dropped outside a slot (e.g., back to the deck), remove from matchedPairs
      if (draggingCard && draggingCard.wasDropped) {
        setMatchedPairs(prev => {
          const newMatchedPairs = { ...prev }
          Object.keys(newMatchedPairs).forEach(slotId => {
            if (newMatchedPairs[slotId]?.id === draggingCard.id) {
              delete newMatchedPairs[slotId]
            }
          })
          return newMatchedPairs
        })
      }

      setDraggingCard(null) // Reset dragging if dropped outside
    }
  }

  // Check for correct matches
  const submitMatches = async () => {
    setCheckingMatches(true)
    const pairs = Object.entries(matchedPairs).map(([slotId, card]) => ({
      backgroundImageId: slotId,
      cardImageId: card.id,
    }))

    const orderedPairs = backgrounds.map(background => ({
      backgroundImageId: background.id,
      cardImageId: matchedPairs[background.id]?.id,
    }))

    const choices = orderedPairs.map(pair => pair.cardImageId).filter(Boolean)

    switch (gameType) {
      case GAME_TYPE.PAIRS:
        try {
          const resMatchDrop = await submitMatchDrop({
            breakId: breakId,
            stepId: step.id,
            dragId: gameData.id,
            pairs,
          })
          setValidatedPairs(resMatchDrop.detail.validations)
        } catch (error) {
          console.error('BR-9645411: Error submitting ranking', error)
        }
        break
      case GAME_TYPE.ORDER:
        try {
          const resSubmitOrder = await submitOrder({
            breakId: breakId,
            stepId: step.id,
            dragId: gameData.id,
            choices,
          })
          setValidatedPairs(resSubmitOrder.detail.validations)
        } catch (error) {
          console.error('BR-9645413: Error submitting ranking', error)
        }
        break
      case GAME_TYPE.RANKING:
        try {
          const resSubmitRanking = await submitRanking({
            breakId: breakId,
            stepId: step.id,
            dragId: gameData.id,
            choices,
          })
          setValidatedPairs(resSubmitRanking.detail.validations)
        } catch (error) {
          console.error('BR-9645417: Error submitting ranking', error)
        }
        break
      default:
        console.error('BR-9645419: Invalid game type')
    }

    const { possibleCoinsAwarded = 0 } = step

    setCheckingMatches(false)
    onSubmitMatches({ points: possibleCoinsAwarded })
    toggle()
  }

  const uniqueUsedDraggable = [...new Set(Object.values(matchedPairs))]

  const duplicatedDraggableId = cardId => {
    const cardIds = Object.values(matchedPairs).map(card => card.id)
    return cardIds.filter(id => id === cardId).length > 1 ? true : false
  }

  const enableSubmit = uniqueUsedDraggable.length === cards.length

  const isCorrectAnswer = backgroundImageId => {
    if (!validatedPairs || gameType === GAME_TYPE.RANKING) return null

    if (gameType === GAME_TYPE.ORDER) {
      return (
        validatedPairs?.find(
          validatedPair =>
            validatedPair?.cardImageId === matchedPairs[backgroundImageId].id &&
            validatedPair?.valid,
        ) || false
      )
    }

    return (
      validatedPairs?.find(
        validatedPair =>
          validatedPair?.pair.backgroundImageId === backgroundImageId &&
          validatedPair?.valid,
      ) || false
    )
  }

  const isWrongAnswer = backgroundImageId => {
    if (!validatedPairs || gameType === GAME_TYPE.RANKING) return null

    if (gameType === GAME_TYPE.ORDER) {
      return (
        validatedPairs?.find(
          validatedPair =>
            validatedPair?.cardImageId === matchedPairs[backgroundImageId].id &&
            !validatedPair?.valid,
        ) || false
      )
    }

    return (
      validatedPairs?.find(
        validatedPair =>
          validatedPair?.pair.backgroundImageId === backgroundImageId &&
          !validatedPair?.valid,
      ) || false
    )
  }

  const resetSlot = clickedSlotId => {
    setMatchedPairs(
      Object.fromEntries(
        Object.entries(matchedPairs).filter(
          ([slotId]) => slotId !== clickedSlotId,
        ),
      ),
    )
    setValidatedPairs(null)
  }

  const gameFinished = !!validatedPairs?.length

  const getCorrectCardId = backgroundImageId => {
    if (gameType === GAME_TYPE.PAIRS) {
      return validatedPairs?.find(
        pair => pair.pair.backgroundImageId === backgroundImageId,
      )?.pair.correctCardId
    }

    return validatedPairs?.find(
      pair => pair.cardImageId === matchedPairs[backgroundImageId]?.id,
    )?.correctCardImageId
  }

  return (
    <div className="drag_drop_container_match">
      <EndGameModal
        gameType={gameType}
        open={showModal}
        onClose={toggle}
        totalNumber={backgrounds.length}
        correctNumber={validatedPairs?.filter(pair => pair.valid).length}
        everyCorrect={validatedPairs?.every(pair => pair.valid)}
      />
      <DragDropGameHeading
        gameType={gameType}
        startDescription={stacks[0].startBody}
        startTitle={stacks[0].startTitle}
      />
      <DndContext
        collisionDetection={fixCursorSnapOffset}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDraggingCard(null)}
      >
        {/* Conditionally render either a grid or slider depending on screen size */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: '100%',
            width: '100%',
          }}
        >
          <div
            className="drag-drop-grid-container"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)', // 4 items per row
              maxWidth: '100%',
              marginBottom: '20px',
            }}
          >
            {backgrounds.map((background, index) => {
              return (
                <div
                  key={background.id}
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: gameType !== GAME_TYPE.PAIRS ? '110%' : '100%',
                    paddingBottom: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <DroppableSlot
                    wrapperStyle={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    backgroundNumber={index + 1}
                    background={background}
                    key={background.id}
                    showCorrectAnswer={showCorrectAnswer}
                    validatedPairs={validatedPairs}
                    droppedCard={matchedPairs[background.id]}
                    isCorrectAnswer={isCorrectAnswer(
                      GAME_TYPE.PAIRS
                        ? background.id
                        : matchedPairs[background.id]?.id,
                    )}
                    isWrongAnswer={isWrongAnswer(
                      GAME_TYPE.PAIRS
                        ? background.id
                        : matchedPairs[background.id]?.id,
                    )}
                    isEditorial={isEditorial}
                    checkingMatches={checkingMatches}
                    resetSlot={resetSlot}
                    gameFinished={!!validatedPairs?.length}
                    gameType={gameType}
                    draggableCard={
                      matchedPairs[background.id] ? (
                        <DraggableCard
                          gameType={gameType}
                          card={
                            showCorrectAnswer
                              ? {
                                  id: getCorrectCardId(background.id),
                                }
                              : matchedPairs[background.id]
                          }
                          wasUsed // Card has been dropped in the top list
                          droppedCard
                          style={{
                            width: '100%',
                            height: '100%',
                          }}
                        />
                      ) : null
                    }
                    isOccupied={!!matchedPairs[background.id]} // Track if the slot is occupied
                    isComingFromBottom={!!draggingCard?.isComingFromBottom} // Check if card is coming from bottom
                    isDuplicateCardId={duplicatedDraggableId(
                      matchedPairs[background.id]?.id,
                    )}
                  />
                </div>
              )
            })}
          </div>
        </div>

        {/* Draggable card list */}
        {!gameFinished && (
          <>
            <span
              className={`draggable-list-title ${isEditorial && 'editorial'}`}
            >
              {t('dragAndDrop:dragFromHere')}
            </span>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <ScrollableList showArrowButtons showButtons>
                {cards.map(card => {
                  const isInMatchedPairs = Object.values(matchedPairs).some(
                    matchedCard => matchedCard.id === card.id,
                  )

                  // If card is in a slot, show a non-draggable placeholder to avoid duplicate IDs
                  if (isInMatchedPairs) {
                    return (
                      <CardPlaceholder
                        key={`${card.id}-${card.shuffleIndex}-placeholder`}
                        card={card}
                        style={{
                          width: draggableCardsDimensions.width,
                          height: draggableCardsDimensions.height,
                        }}
                        gameType={gameType}
                      />
                    )
                  }

                  // Otherwise, show the draggable card
                  return (
                    <DraggableCard
                      key={`${card.id}-${card.shuffleIndex}`}
                      wasUsed={false}
                      card={card}
                      isInBottomList
                      style={{
                        width: draggableCardsDimensions.width,
                        height: draggableCardsDimensions.height,
                      }}
                      gameType={gameType}
                    />
                  )
                })}
              </ScrollableList>
            </div>
          </>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {validatedPairs ? (
            socialShareComponent
          ) : (
            <button
              disabled={!enableSubmit}
              className="btn"
              onClick={submitMatches}
              style={{ marginTop: '20px' }}
            >
              {t('common:submit')}
            </button>
          )}
        </div>

        {/* Overlay for the dragged item */}
        <DragOverlay
          modifiers={[snapCenterToCursor]}
          dropAnimation={{
            duration: 200,
            easing: 'ease',
          }}
        >
          {draggingCard ? (
            <StackedImage
              style={{
                width: draggableCardsDimensions.width,
                height: draggableCardsDimensions.height,
                border: '2px solid black',
              }}
              image={draggingCard}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      <div className="btn-options-wrapper">
        {(!gameFinished || checkingMatches) && (
          <button
            className="btn-options"
            onClick={restartGame}
            style={{ marginTop: '20px' }}
          >
            {t('dragAndDrop:clickToRestart')}
          </button>
        )}

        {gameFinished && gameType !== GAME_TYPE.RANKING && (
          <button
            className="btn-options"
            onClick={() => setShowCorrectAnswer(prev => !prev)}
          >
            {showCorrectAnswer
              ? t('dragAndDrop:showMyAnswers')
              : t('dragAndDrop:showCorrectAnswers')}
          </button>
        )}
        {skipButton}
      </div>

      {nextStepButton}
    </div>
  )
}

export default DragAndDropGame
